import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./CustomerCare.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { BASE_CUSTOMER_CARE_URL } from "~/helper/apiService";

const cx = classNames.bind(styles);

function ReturnStatusPage() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // "overview", "returned", "notReturned"
  const [statistics, setStatistics] = useState(null);
  const [returnedList, setReturnedList] = useState([]);
  const [notReturnedList, setNotReturnedList] = useState([]);
  const { showToast } = useContext(ToastContext);

  useEffect(() => {
    fetchReturnStatus();
  }, []);

  const fetchReturnStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/return-status`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.statistics);
        setReturnedList(data.returned || []);
        setNotReturnedList(data.notReturned || []);
      } else {
        showToast(data.error || "Lỗi khi lấy thống kê", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAndUpdate = async () => {
    setLoading(true);
    try {
      // Gọi endpoint return-status (đã tự động update sheets khi load)
      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/return-status`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.statistics);
        setReturnedList(data.returned || []);
        setNotReturnedList(data.notReturned || []);
        showToast("Đã refresh và cập nhật sheet 'Đã quay lại' và 'Chưa quay lại' thành công", "success");
      } else {
        showToast(data.error || "Lỗi khi refresh và cập nhật", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityClass = (days) => {
    if (days >= 90) return "severity-lost";
    if (days >= 60) return "severity-cold";
    if (days >= 30) return "severity-risk";
    return "severity-urgent";
  };

  const renderTable = (list, showExtraColumns = false) => {
    if (list.length === 0) {
      return <div className={cx("no-data")}>Không có dữ liệu</div>;
    }

    return (
      <>
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              {showExtraColumns && (
                <>
                  <th className={cx("th")}>Ngày CSKH gần nhất</th>
                  <th className={cx("th")}>Số ngày từ CSKH</th>
                  <th className={cx("th")}>Số lần đã chăm sóc</th>
                  <th className={cx("th")}>Nhân viên</th>
                  <th className={cx("th")}>Ghi chú</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {list.map((customer, index) => (
              <tr key={index} className={cx("tr")}>
                <td className={cx("td")}>{customer.phone}</td>
                <td className={cx("td")}>{customer.totalVisits}</td>
                <td className={cx("td")}>{customer.lastVisitDate}</td>
                <td className={cx("td")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</td>
                <td className={cx("td", "severity")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </td>
                {showExtraColumns && (
                  <>
                    <td className={cx("td")}>{customer.lastCareDate || "-"}</td>
                    <td className={cx("td")}>{customer.daysSinceLastCare !== null ? customer.daysSinceLastCare : "-"}</td>
                    <td className={cx("td")}>{customer.careCount !== undefined ? customer.careCount : "-"}</td>
                    <td className={cx("td")}>{customer.careStaff || "-"}</td>
                    <td className={cx("td")}>{customer.careNote || "-"}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <div className={cx("mobile-card-container")}>
          {list.map((customer, index) => (
            <div key={index} className={cx("mobile-card")}>
              <div className={cx("mobile-card-header")}>
                <div className={cx("mobile-card-title")}>{customer.phone}</div>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Tổng lượt đi:</span>
                <span className={cx("mobile-card-value")}>{customer.totalVisits}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ngày đi gần nhất:</span>
                <span className={cx("mobile-card-value")}>{customer.lastVisitDate}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số ngày chưa quay lại:</span>
                <span className={cx("mobile-card-value")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Mức độ:</span>
                <span className={cx("mobile-card-value")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </span>
              </div>
              {showExtraColumns && (
                <>
                  <div className={cx("mobile-card-row")}>
                    <span className={cx("mobile-card-label")}>Ngày CSKH gần nhất:</span>
                    <span className={cx("mobile-card-value")}>{customer.lastCareDate || "-"}</span>
                  </div>
                  <div className={cx("mobile-card-row")}>
                    <span className={cx("mobile-card-label")}>Số ngày từ CSKH:</span>
                    <span className={cx("mobile-card-value")}>{customer.daysSinceLastCare !== null ? customer.daysSinceLastCare : "-"}</span>
                  </div>
                  <div className={cx("mobile-card-row")}>
                    <span className={cx("mobile-card-label")}>Số lần đã chăm sóc:</span>
                    <span className={cx("mobile-card-value")}>{customer.careCount !== undefined ? customer.careCount : "-"}</span>
                  </div>
                  <div className={cx("mobile-card-row")}>
                    <span className={cx("mobile-card-label")}>Nhân viên:</span>
                    <span className={cx("mobile-card-value")}>{customer.careStaff || "-"}</span>
                  </div>
                  <div className={cx("mobile-card-row")}>
                    <span className={cx("mobile-card-label")}>Ghi chú:</span>
                    <span className={cx("mobile-card-value")}>{customer.careNote || "-"}</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={cx("customer-care")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Thống Kê Quay Lại Sau Chăm Sóc</h2>
        <div className={cx("actions")}>
          <button
            className={cx("button", "refresh-button")}
            onClick={handleRefreshAndUpdate}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh & Update Sheets
          </button>
        </div>
      </div>

      <div className={cx("tabs")}>
        <button
          className={cx("tab", { active: activeTab === "overview" })}
          onClick={() => setActiveTab("overview")}
        >
          Tổng Quan
        </button>
        <button
          className={cx("tab", { active: activeTab === "returned" })}
          onClick={() => setActiveTab("returned")}
        >
          Đã Quay Lại ({statistics?.totalReturned || 0})
        </button>
        <button
          className={cx("tab", { active: activeTab === "notReturned" })}
          onClick={() => setActiveTab("notReturned")}
        >
          Chưa Quay Lại ({statistics?.totalNotReturned || 0})
        </button>
      </div>

      <div className={cx("tab-content")}>
        {activeTab === "overview" && (
          <>
            {statistics ? (
              <div className={cx("statistics-overview")}>
                <div className={cx("stat-item", "total")}>
                  <span className={cx("stat-label")}>Tổng số khách đã chăm sóc</span>
                  <span className={cx("stat-value")}>{statistics.totalCared} khách</span>
                </div>
                <div className={cx("stat-item")} style={{ background: "#e8f5e9" }}>
                  <span className={cx("stat-label")}>✅ Đã quay lại sau chăm sóc</span>
                  <span className={cx("stat-value")}>{statistics.totalReturned} khách</span>
                </div>
                <div className={cx("stat-item")} style={{ background: "#fff3e0" }}>
                  <span className={cx("stat-label")}>⏳ Chưa quay lại sau chăm sóc</span>
                  <span className={cx("stat-value")}>{statistics.totalNotReturned} khách</span>
                </div>
                <div className={cx("stat-item")}>
                  <span className={cx("stat-label")}>Tỷ lệ quay lại</span>
                  <span className={cx("stat-value")}>
                    {statistics.totalCared > 0
                      ? ((statistics.totalReturned / statistics.totalCared) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
            ) : (
              <div className={cx("no-data")}>Chưa có dữ liệu. Vui lòng nhấn Refresh & Update Sheets để cập nhật.</div>
            )}
          </>
        )}

        {activeTab === "returned" && (
          <div className={cx("table-container")}>
            {renderTable(returnedList, true)}
          </div>
        )}

        {activeTab === "notReturned" && (
          <div className={cx("table-container")}>
            {renderTable(notReturnedList, true)}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReturnStatusPage;
